import React, { useEffect, useMemo, useState } from "react";
import * as url from "../../../../helpers/url_helper";
import { useSelector } from "react-redux";
import ChipsDataTable from "./ChipsDetail/ChipsDataTable";
import DataTable from "react-data-table-component";
import { convertProfileLocalDate } from "../../../../Utils/configController";
import { toast } from "react-toastify";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Offcanvas,
  OffcanvasHeader,
  OffcanvasBody,
} from "reactstrap";
import { ActionButton } from "../../../../components/Common/button";
import { MqttIcon, OtaIcon, PingIcon } from "../../../../assets/svg";
import { OPERATE_MQTT } from "../../../../helpers/socketEvent";
import { useSocket } from "../../../../Utils/socket";
import OtaModel from "./OtaModal";

export interface ChipsControlProps {
  userId: any;
  selectedPremise?: any;
}
const userInfo = JSON.parse(localStorage.getItem("userInfo") || "{}");

const formatElementLabel = (name?: string, fallback = "Element 0") => {
  if (!name) return fallback;
  const match = name.match(/0x([0-9A-Fa-f]+)/i);
  if (!match) return name;
  const decimal = parseInt(match[1], 16);
  if (Number.isNaN(decimal)) return name;
  return `Node ID: ${decimal}`;
};

type HighlightNode = {
  uuid?: string;
  nodeId?: number | string;
};

const getNodeUuid = (node: any) => node?.UUID || node?.uuid || "";

const normalizeUuid = (value?: string) =>
  (value || "").toUpperCase().replace(/-/g, "");

const getNodeAddresses = (node: any) =>
  [
    node?.unicast,
    node?.addr,
    node?.address,
    node?.nodeId,
    ...(node?.elements ?? []).map((element: any) => element?.addr),
  ].filter((value) => value != null && value !== "");

const doesNodeMatch = (node: any, highlight: HighlightNode | null) => {
  if (!highlight) return false;

  if (highlight.uuid) {
    const nodeUuid = getNodeUuid(node);
    if (nodeUuid && normalizeUuid(nodeUuid) === normalizeUuid(highlight.uuid)) {
      return true;
    }
  }

  if (highlight.nodeId != null && highlight.nodeId !== "") {
    const targetId = Number(highlight.nodeId);
    if (!Number.isNaN(targetId)) {
      return getNodeAddresses(node).some((addr) => Number(addr) === targetId);
    }
  }

  return false;
};

const getNodeElementId = (node: any, index: number) => {
  const nodeUuid = getNodeUuid(node);
  if (nodeUuid) {
    return `node-uuid-${normalizeUuid(nodeUuid)}`;
  }

  const nodeAddress = getNodeAddresses(node)[0];
  return `node-id-${nodeAddress ?? index}`;
};

const GatewayList = ({ userId, selectedPremise }: ChipsControlProps) => {
  const selectedRooms = useSelector(
    (state: any) => state.APIResponse.selectedRooms,
  );
  const socket: any = useSocket();
  const [loading, setLoading] = useState<boolean>(false);
  const [isReload, setReload] = useState<boolean>(false);

  const [selectedRows, setSelectedRows] = useState<any[]>([]);

  const [isOpneRequestModel, setOpenRequestModel] = useState<boolean>(false);
  const [requestData, setRequestData] = useState<any>({});
  const [otaSlave, setOtaSlave] = useState<any>({});
  const [isOtaModalOpen, setIsOtaModalOpen] = useState(false);
  const [networkInfo, setNetworkInfo] = useState<any>({});
  const [isNetworkOffcanvasOpen, setIsNetworkOffcanvasOpen] = useState(false);
  const [highlightNode, setHighlightNode] = useState<HighlightNode | null>(
    null,
  );
  const [showAllNodes, setShowAllNodes] = useState(false);

  const HIDDEN_NODE_COUNT = 10;

  const networkNodes = networkInfo?.networkInfo?.nodes;

  const displayedNodes = useMemo(() => {
    const nodes = networkNodes ?? [];
    return showAllNodes ? nodes : nodes.slice(HIDDEN_NODE_COUNT);
  }, [networkNodes, showAllNodes]);

  const allNetworkNodes = networkNodes ?? [];
  const hiddenNodesCount = showAllNodes
    ? 0
    : Math.min(HIDDEN_NODE_COUNT, allNetworkNodes.length);

  const handleNetworkInfoClick = (row: any) => {
    setHighlightNode(null);
    setShowAllNodes(false);
    setNetworkInfo(row ?? {});
    setIsNetworkOffcanvasOpen(true);
  };

  const handleDriverNodeClick = (gatewayRow: any, driverRow: any) => {
    if (driverRow?.nodeId == null && !driverRow?.uuid) {
      toast.info("Node details not available for this driver.");
      return;
    }

    const nodes = gatewayRow?.networkInfo?.nodes ?? [];
    const highlight: HighlightNode = {
      uuid: driverRow?.uuid,
      nodeId: driverRow?.nodeId,
    };
    const matchedIndex = nodes.findIndex((node: any) =>
      doesNodeMatch(node, highlight),
    );

    setNetworkInfo(gatewayRow ?? {});
    setHighlightNode(highlight);
    setShowAllNodes(matchedIndex >= 0 && matchedIndex < HIDDEN_NODE_COUNT);
    setIsNetworkOffcanvasOpen(true);
  };

  const toggleNetworkOffcanvas = () => {
    setIsNetworkOffcanvasOpen((prev) => {
      if (prev) {
        setHighlightNode(null);
        setShowAllNodes(false);
      }
      return !prev;
    });
  };

  useEffect(() => {
    if (!isNetworkOffcanvasOpen || !highlightNode) return;

    const timer = setTimeout(() => {
      const nodes = networkInfo?.networkInfo?.nodes ?? [];
      const matchedNode = nodes.find((node: any) =>
        doesNodeMatch(node, highlightNode),
      );

      if (!matchedNode) return;

      const elementId = getNodeElementId(
        matchedNode,
        nodes.indexOf(matchedNode),
      );
      document
        .getElementById(elementId)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 1000);

    return () => clearTimeout(timer);
  }, [isNetworkOffcanvasOpen, highlightNode, networkInfo]);

  const handleCopyKey = (value: string, label: string) => {
    if (!value) return;
    navigator.clipboard.writeText(value);
    toast.success(`${label} copied`);
  };

  const handleViewRequest = (rowData: any) => {
    console.log("rowData.redisInfo: ", rowData);

    setOpenRequestModel(true);
    setRequestData(rowData.redisInfo);
  };
  const toggleRequestModel = () => {
    setOpenRequestModel(!isOpneRequestModel);
  };

  const handlePingClick = (row: any) => {
    if (row !== undefined) {
      const reqObj = {
        slave: row?.slave,
        tag: `airpanel-${userInfo?.id}`,
        cmd: "PG",
      };
      socket.emit(OPERATE_MQTT, JSON.stringify(reqObj));
      handleRefresh();
      toast.success(`Ping sent to slave ${row?.slave}`);
    }
  };
  const handleMQTTData = (row: any) => {
    row?.slave
      ? window.open(`/debug-device-details/${row?.slave}`, "_blank")
      : "";
  };
  const handleOtaClick = (row: any) => {
    if (row !== undefined) {
      setIsOtaModalOpen(!isOtaModalOpen);
      setOtaSlave(row);
    }
  };
  const columns = [
    {
      name: "Slave",
      selector: (row: any) => row.slave || "-",
      sortable: true,
      grow: 1.5,
      minWidth: "150px",
      wrap: false,
    },

    {
      name: "Name",
      selector: (row: any) => (row?.name ? row.name : "-"),
      sortable: true,
      grow: 0.8,
      minWidth: "90px",
      wrap: false,
    },
    // {
    //   name: "Room",
    //   selector: (row: any) => row.roomName || "-",
    //   sortable: true,
    //   wrap: true,
    // },
    // {
    //   name: "HW-V",
    //   selector: (row: any) => row.hwVersion || "-",
    //   sortable: true,
    //   grow: 0.5,

    //   wrap: true,
    // },
    // {
    //   name: "WI-V",
    //   selector: (row: any) => row.wifiVersion || "-",
    //   sortable: true,
    //   wrap: true,
    //   grow: 0.5,
    // },

    {
      name: "WI-V(HW-V)",
      selector: (row: any) =>
        `${row.wifiVersion || "-"} (${row.hwVersion || "-"})`,
      sortable: true,
      grow: 1,
      minWidth: "130px",
      wrap: false,
    },
    {
      name: "Network",
      cell: (row: any) => (
        <button
          className="btn btn-sm btn-primary"
          style={{
            padding: "2px 6px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            whiteSpace: "nowrap",
          }}
          onClick={() => handleNetworkInfoClick(row)}
        >
          Info
        </button>
      ),
      sortable: false,
      grow: 0.6,
      minWidth: "85px",
      wrap: false,
    },
    {
      name: "Status",
      selector: (row: any) => (row.isOnline ? "Online" : "Offline"),
      cell: (row: any) =>
        row.isOnline === true ? (
          <span className="font-size-11 badge-soft-success badge bg-secondary">
            online
          </span>
        ) : (
          <span className="font-size-11 badge-soft-danger badge bg-secondary">
            Offline
          </span>
        ),
      sortable: true,
      grow: 0.7,
      minWidth: "90px",
      wrap: false,
    },
    {
      name: "Drivers No.",
      selector: (row: any) => row.noOfAirDriver || "-",
      sortable: true,
      grow: 0.7,
      minWidth: "95px",
      wrap: false,
    },
    {
      name: "Online Drivers",
      selector: (row: any) => row?.total_OnlineDrivers || "-",
      sortable: true,
      grow: 0.8,
      minWidth: "115px",
      wrap: false,
    },
    {
      name: "Offline Drivers",
      selector: (row: any) => row.offlineDrivers || "-",
      sortable: true,
      grow: 0.9,
      minWidth: "120px",
      wrap: false,
    },

    {
      name: "Signal",
      selector: (row: any) => (row.signalStrength ? row.signalStrength : 0),
      cell: (row: any) => (
        <div
          className="d-flex align-items-center gap-2"
          style={{ whiteSpace: "nowrap" }}
        >
          <span style={{ fontWeight: "bold", color: "black" }}>
            {row.signalStrength || "-"}
          </span>
          <span className="text-muted">({row.ssid || "-"})</span>
        </div>
      ),
      sortable: true,
      grow: 1.5,
      minWidth: "170px",
      wrap: false,
    },

    {
      name: "Actions",
      grow: 2.2,
      minWidth: "240px",
      cell: (row: any) => (
        <div className="d-flex align-items-center gap-2 flex-nowrap">
          <ActionButton
            id="PingIconButton"
            title="Ping"
            icon={<PingIcon />}
            onClick={() => handlePingClick(row)}
          />
          <ActionButton
            id="MQTTLogsIconButton"
            title="MQTT Logs"
            icon={<MqttIcon />}
            onClick={() => handleMQTTData(row)}
          />
          <ActionButton
            id="OTAIconButton"
            title="OTA"
            icon={<OtaIcon />}
            onClick={() => handleOtaClick(row)}
          />
          <button
            className="btn btn-sm btn-primary"
            style={{
              padding: "2px 6px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              whiteSpace: "nowrap",
            }}
            onClick={() => handleViewRequest(row)}
          >
            More
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
    },
  ];

  const handleRefresh = () => {
    setReload((prev) => !prev);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      handleRefresh();
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  return (
    <div>
      {/* VIEW REQUEST MODEL */}
      <Modal
        isOpen={isOpneRequestModel}
        role="dialog"
        autoFocus={true}
        centered={true}
        className="exampleModal"
        // tabIndex="-1"
        toggle={toggleRequestModel}
      >
        <div className="modal-content">
          <ModalHeader style={{ justifyContent: "center" }}>
            {"Request"}
          </ModalHeader>
          <ModalBody>
            {/* {requestData} */}
            <pre style={{ whiteSpace: "pre-wrap" }}>
              {JSON.stringify(requestData, null, 2)}
            </pre>
          </ModalBody>
          <ModalFooter style={{ justifyContent: "center" }}>
            <button
              className="btn btn-dark"
              type="submit"
              onClick={() => setOpenRequestModel(false)}
            >
              Ok
            </button>
          </ModalFooter>
        </div>
      </Modal>
      {isOtaModalOpen && (
        <OtaModel
          headerTitle="OTA"
          otaSlave={otaSlave}
          cancleBtnClick={() => setIsOtaModalOpen(false)}
        />
      )}
      <style>
        {`
          .gateway-thin-scroll {
            scrollbar-width: thin;
            scrollbar-color: #b8c2cc transparent;
          }
          .gateway-thin-scroll::-webkit-scrollbar {
            width: 4px;
            height: 4px;
          }
          .gateway-thin-scroll::-webkit-scrollbar-thumb {
            background-color: #b8c2cc;
            border-radius: 4px;
          }
          .gateway-thin-scroll::-webkit-scrollbar-track {
            background: transparent;
          }
          .gateway-node-highlight {
            outline: 3px solid #0d6efd !important;
            outline-offset: 2px;
            box-shadow: 0 0 0 4px rgba(13, 110, 253, 0.25) !important;
          }
        `}
      </style>
      <Offcanvas
        isOpen={isNetworkOffcanvasOpen}
        toggle={toggleNetworkOffcanvas}
        direction="end"
        style={{ width: "90%" }}
      >
        <OffcanvasHeader
          toggle={toggleNetworkOffcanvas}
          className="d-flex align-items-start justify-content-between w-100"
        >
          <div className="d-flex align-items-center justify-content-between w-100 pe-3 gap-4">
            <h5 className="modal-title mb-0" style={{ fontWeight: 800 }}>
              Network Info
              {networkInfo?.slave ? ` (${networkInfo.slave})` : ""}
            </h5>
            <div className="d-flex align-items-center gap-4 flex-wrap">
             
              {[
                { label: "Net Key", value: networkInfo?.netKey },
                { label: "App Key", value: networkInfo?.appKey },
              ].map(({ label, value }) => (
                <div key={label} className="d-flex align-items-center gap-2">
                  <span className="text-muted fw-semibold small">{label}</span>

                  {value ? (
                    <div
                      className="d-flex align-items-center gap-2 px-2 py-1 rounded"
                      style={{
                        background: "#eef3ff",
                        cursor: "pointer",
                        maxWidth: "300px",
                      }}
                      onClick={() => handleCopyKey(value, label)}
                      title="Click to copy"
                    >
                      <code
                        className="text-primary mb-0"
                        style={{
                          fontSize: "10px",
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {value}
                      </code>

                      <i className="bx bx-copy text-primary"></i>
                    </div>
                  ) : (
                    <span>-</span>
                  )}
                </div>
              ))}
           
                <div
                  className="form-check d-flex align-items-center justify-content-center gap-2 mb-0"
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="showAllNodes"
                    checked={showAllNodes}
                    onChange={(e) => setShowAllNodes(e.target.checked)}
                  />
                  <label
                    className="form-check-label small fw-semibold mb-0 mt-1"
                    htmlFor="showAllNodes"
                  >
                    Show all ({allNetworkNodes.length})
                  </label>
                </div>
            </div>
          </div>
        </OffcanvasHeader>

        <OffcanvasBody
          className="d-flex flex-column flex-grow-1 min-h-0 p-0 gateway-thin-scroll"
          style={{
            overflow: "auto",
            backgroundColor: "#f7f7fa",
            minHeight: 0,
          }}
        >
          {allNetworkNodes.length > 0 ? (
            <div className="p-3 d-flex flex-column gap-3">
              {displayedNodes.length === 0 ? (
                <div className="text-muted small">
                  No nodes to display beyond the hidden first {hiddenNodesCount}
                  . Check &quot;Show all nodes&quot; to view them.
                </div>
              ) : (
                <div className="d-flex flex-wrap gap-3 align-items-start">
                  {displayedNodes.map((node: any, visibleIndex: number) => {
                    const nodeIndex = showAllNodes
                      ? allNetworkNodes.indexOf(node)
                      : HIDDEN_NODE_COUNT + visibleIndex;
                    const isHighlighted = doesNodeMatch(node, highlightNode);

                    return (
                      <div
                        key={getNodeElementId(node, nodeIndex)}
                        id={getNodeElementId(node, nodeIndex)}
                        className={`card shadow-sm d-flex flex-column flex-shrink-0 ${
                          isHighlighted ? "gateway-node-highlight" : "border-0"
                        }`}
                        style={{
                          borderRadius: "10px",
                          width: "400px",
                          height: "500px",
                          marginBottom: 0,
                        }}
                      >
                        <div className="card-body p-3 d-flex flex-column h-100 overflow-hidden">
                          <div
                            className="d-flex align-items-center flex-nowrap gap-1 mb-2 flex-shrink-0 gateway-thin-scroll"
                            style={{
                              width: "100%",
                              minWidth: 0,
                              overflowX: "auto",
                              overflowY: "hidden",
                            }}
                          >
                            {(node?.elements ?? []).length > 0 ? (
                              <>
                                <span
                                  className="badge px-2 py-1 fw-semibold flex-shrink-0"
                                  style={{
                                    backgroundColor: "#d4edda",
                                    color: "#155724",
                                    fontSize: "11px",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {formatElementLabel(node.elements[0]?.name)}
                                </span>
                                {node.elements
                                  .slice(1)
                                  .map((element: any, elIdx: number) => (
                                    <React.Fragment key={elIdx}>
                                      <i className="bx bx-right-arrow-alt text-muted flex-shrink-0" />
                                      <span
                                        className="badge px-2 py-1 fw-semibold flex-shrink-0"
                                        style={{
                                          backgroundColor: "#fff3cd",
                                          color: "#856404",
                                          fontSize: "11px",
                                          whiteSpace: "nowrap",
                                        }}
                                      >
                                        {formatElementLabel(
                                          element?.name,
                                          `Element ${elIdx + 1}`,
                                        )}
                                      </span>
                                    </React.Fragment>
                                  ))}
                              </>
                            ) : (
                              <span className="fw-semibold small text-truncate">
                                {node?.name || `Node ${nodeIndex + 1}`}
                              </span>
                            )}
                          </div>
                          <pre
                            className="flex-grow-1 mb-0 gateway-thin-scroll"
                            style={{
                              whiteSpace: "pre",
                              fontSize: "11px",
                              lineHeight: 1.5,
                              backgroundColor: "#fff",
                              padding: "8px",
                              borderRadius: "8px",
                              border: "1px solid #e9ecef",
                              minHeight: 0,
                              minWidth: 0,
                              width: "100%",
                              overflowX: "auto",
                              overflowY: "auto",
                            }}
                          >
                            <div className="position-relative">
                              <span
                                className="position-absolute top-0 end-0 badge bg-light text-primary border"
                                style={{ cursor: "pointer", fontSize: "14px" }}
                                onClick={() =>
                                  handleCopyKey(
                                    JSON.stringify(node, null, 2),
                                    "Node object",
                                  )
                                }
                              >
                                <i className="bx bx-copy"></i>
                              </span>
                            </div>

                            {JSON.stringify(node, null, 2)}
                          </pre>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ) : (
            <div className="text-center text-muted py-5 w-100">
              No nodes available
            </div>
          )}
        </OffcanvasBody>
      </Offcanvas>
      <ChipsDataTable
        apiURL={`${url.BASE_URL}${url.GET_GATEWAY_LIST}`}
        title="Gateway List"
        customStyles={customStyles}
        selectedRoomID={selectedRooms}
        columns={columns}
        isReload={isReload}
        expandableRows={true}
        ExpandedComponent={(props: any) => (
          <ExpandedComponent
            {...props}
            onNodeIdClick={(driver: any) =>
              handleDriverNodeClick(props.data, driver)
            }
          />
        )}
        ExpandCollapseIcon={ExpandCollapseIcon}
        idName={"Gatewaylist"}
        loading={loading}
        setLoading={setLoading}
        selectedRows={selectedRows}
        setSelectedRows={setSelectedRows}
        userId={userId}
        selectedPremise={selectedPremise}
      />
    </div>
  );
};

export default GatewayList;

export const ExpandedComponent = ({
  data,
  onNodeIdClick,
}: {
  data: any;
  onNodeIdClick?: (driver: any) => void;
}) => {
  const [isGroupModalOpen, setIsGroupModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState<any>(null);
const [nodeSearch, setNodeSearch] = useState("");
const [filterOnline, setFilterOnline] = useState(false);
const [filterOffline, setFilterOffline] = useState(false);
const [filterPowerOn, setFilterPowerOn] = useState(false);
const [filterPowerOff, setFilterPowerOff] = useState(false);

  const handleNodeIdClick = (row: any) => {
    if (row?.driverType === "group") {
      setSelectedGroup(row);
      setIsGroupModalOpen(true);
      return;
    }

    // Existing behavior for normal drivers
    onNodeIdClick?.(row);
  };

  const toggleGroupModal = () => {
    setIsGroupModalOpen((prev) => !prev);
  };
  const driversColumns: any[] = [
    {
      name: "Name",
      selector: (row: any) => row?.name || "-",
      sortable: true,
      grow: 1.5,
      wrap: true,
    },
    // {
    //   name: "Slave",
    //   selector: (row: any) => row?.slave || "-",
    //   sortable: true,
    //   grow: 1.5,
    //   wrap: true,
    // },
    {
      name: "Room",
      selector: (row: any) => row.roomName || "-",
      sortable: true,
      wrap: true,
    },
    {
      name: "Node ID",
      selector: (row: any) => row?.nodeId ?? "-",
      cell: (row: any) =>
        row?.nodeId != null && row?.nodeId !== "" ? (
          <span
            style={{
              cursor: "pointer",
              color: "#0d6efd",
              fontWeight: 600,
            }}
            title="View node in network info"
            onClick={() => handleNodeIdClick(row)}
          >
            {row.nodeId}
          </span>
        ) : (
          <span>-</span>
        ),
      sortable: true,
      grow: 0.8,
      wrap: true,
    },
    {
      name: "macAddress",
      selector: (row: any) => row?.macAddress || "-",
      sortable: true,
      wrap: true,
      cell: (row:any) => (
      <span
       style={{
           fontSize: "8px"
        }}>
        {row?.macAddress}
      </span>
      ) 
    },
    {
      name: "uuidDigit",
      selector: (row: any) => row?.uuidDigit || "-",
      sortable: true,
      wrap: true,
    },
    {
      name: "UUID",
      selector: (row: any) => row?.uuid || "-",
      cell: (row: any) =>
        row?.uuid ? (
          <span
            style={{
              fontSize: "8px",
              cursor: "pointer",
              color: "#0d6efd",
            }}
            title="Click to copy"
            onClick={() => {
              navigator.clipboard.writeText(row?.uuid);
              toast.success("UUID copied");
            }}
          >
            {row?.uuid}
          </span>
        ) : (
          <span>-</span>
        ),
      sortable: true,
      grow: 1,
      wrap: true,
    },
    {
      name: "Device Key",
      selector: (row: any) => row?.devKey || "-",
      cell: (row: any) =>
        row?.devKey ? (
          <span
            style={{
              fontSize: "8px",
              cursor: "pointer",
              color: "#0d6efd",
            }}
            title="Click to copy"
            onClick={() => {
              navigator.clipboard.writeText(row?.devKey);
              toast.success("Device Key copied");
            }}
          >
            {row?.devKey}
          </span>
        ) : (
          <span>-</span>
        ),
      sortable: true,
      grow: 1,
      wrap: true,
    },
    {
      name: "Type",
      selector: (row: any) => row?.deviceTypeTag || "-",
      sortable: true,
      grow: 0.5,
      wrap: true,
    },
    {
      name: "Configured",
      selector: (row: any) => (row?.isConfigured ? "Yes" : "No"),
      cell: (row: any) =>
        row?.isConfigured ? (
          <span className="font-size-11 badge-soft-success badge bg-success">
            Yes
          </span>
        ) : (
          <span className="font-size-11 badge-soft-danger badge bg-danger">
            No
          </span>
        ),
      sortable: true,
      grow: 0.8,
      wrap: true,
    },
    {
      name: "Status",
      selector: (row: any) => (row?.isOnline ? "Online" : "Offline"),
      cell: (row: any) =>
        row?.isOnline ? (
          <span className="font-size-11 badge-soft-success badge bg-success">
            Online
          </span>
        ) : (
          <span className="font-size-11 badge-soft-danger badge bg-danger">
            Offline
          </span>
        ),
      sortable: true,
      grow: 0.5,
      wrap: true,
    },
    {
      name: "Power",
      selector: (row: any) => (row?.isOn ? "ON" : "OFF"),
      cell: (row: any) =>
        row?.isOn ? (
          <span className="font-size-11 badge-soft-success badge bg-success">
            ON
          </span>
        ) : (
          <span className="font-size-11 badge-soft-danger badge bg-danger">
            OFF
          </span>
        ),
      sortable: true,
      grow: 0.5,
      wrap: true,
    },
    {
      name: "Brightness",
      selector: (row: any) => row?.brightness ?? "-",
      sortable: true,
      grow: 0.5,
      wrap: true,
    },
    {
      name: "Temperature",
      selector: (row: any) => row?.temp ?? "-",
      sortable: true,
      grow: 0.5,
      wrap: true,
    },
  ];

  // Create table rows including static counts
  const driversData = data?.drivers || [];

  // Find the individual drivers that belong to a given group row
  const getGroupMembers = (group: any) => {
    if (!group?.nodeIds?.length) return [];
    return driversData.filter(
      (item: any) =>
        item.driverType !== "group" &&
        group.nodeIds.some(
          (groupNodeId: number) => Number(groupNodeId) === Number(item.nodeId)
        )
    );
  };

  // Nested table shown when a group row is expanded
const GroupMembersTable = ({
  members,
}: {
  members: any[];
}) => {
  return (
    <div
      className="mb-4"
      style={{
        maxHeight: "400px",
        overflowY: "auto",
      }}
    >
      <DataTable
        columns={driversColumns}
        data={members}
        dense
        highlightOnHover
        noDataComponent="No drivers found"
        customStyles={customStyles}
      />
    </div>
  );
};

  const groupMembers = useMemo(() => {
    const members = getGroupMembers(selectedGroup);

    return members.filter((member: any) => {
      // Node ID search
      const searchMatch =
        !nodeSearch.trim() ||
        String(member?.nodeId ?? "")
          .toLowerCase()
          .includes(nodeSearch.trim().toLowerCase());

      // Online / Offline filter
      const statusFilterActive = filterOnline || filterOffline;

      const statusMatch =
        !statusFilterActive ||
        (filterOnline && member?.isOnline === true) ||
        (filterOffline && member?.isOnline !== true);

      // Power ON / OFF filter
      const powerFilterActive = filterPowerOn || filterPowerOff;

      const powerMatch =
        !powerFilterActive ||
        (filterPowerOn && member?.isOn === true) ||
        (filterPowerOff && member?.isOn !== true);

      return searchMatch && statusMatch && powerMatch;
    });
  }, [
    selectedGroup,
    nodeSearch,
    filterOnline,
    filterOffline,
    filterPowerOn,
    filterPowerOff,
    driversData,
  ]);
  
  const counts = groupMembers.reduce(
    (acc: any, data: any) => {
      acc.total++;

      if (data?.isOnline === true) {
        acc.online++;
      } else {
        acc.offline++;
      }

      if (data?.isOn === true) {
        acc.powerOn++;
      } else {
        acc.powerOff++;
      }

      return acc;
    },
    {
      total: 0,
      online: 0,
      offline: 0,
      powerOn: 0,
      powerOff: 0,
    }
  ); 
 return (
   <div
     className="p-2"
     style={{
       maxHeight: "500px",
       overflowY: "auto",
     }}
   >
     <DataTable
       columns={driversColumns}
       data={driversData}
       dense
       highlightOnHover
       noDataComponent="No drivers available"
       customStyles={customStyles}
     />
     <Modal
       isOpen={isGroupModalOpen}
       toggle={toggleGroupModal}
       centered
       size="xl"
     >
       <ModalHeader toggle={toggleGroupModal}>
         <div className="d-flex align-items-center gap-2">
           <i className="bx bx-layer fs-4 text-primary"></i>

           <div className="fw-bold">Group Driver details</div>

           {selectedGroup?.nodeId != null && (
             <small className="text-muted">
               Node ID: {selectedGroup.nodeId}
             </small>
           )}
             <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2 mt-2">
               <div className="d-flex gap-3 flex-wrap">
                 <div className="d-flex align-items-center gap-1">
                   <input
                     type="checkbox"
                     className="form-check-input mt-0"
                     checked={filterOnline}
                     onChange={(e) => setFilterOnline(e.target.checked)}
                   />
                   <span className="text-muted small fw-bold">Online</span>
                 </div>

                 <div className="d-flex align-items-center gap-1">
                   <input
                     type="checkbox"
                     className="form-check-input mt-0"
                     checked={filterOffline}
                     onChange={(e) => setFilterOffline(e.target.checked)}
                   />
                   <span className="text-muted small fw-bold">Offline</span>
                 </div>

                 <div className="d-flex align-items-center gap-1">
                   <input
                     type="checkbox"
                     className="form-check-input mt-0"
                     checked={filterPowerOn}
                     onChange={(e) => setFilterPowerOn(e.target.checked)}
                   />
                   <span className="text-muted small fw-bold">Power ON</span>
                 </div>

                 <div className="d-flex align-items-center gap-1">
                   <input
                     type="checkbox"
                     className="form-check-input mt-0"
                     checked={filterPowerOff}
                     onChange={(e) => setFilterPowerOff(e.target.checked)}
                   />
                   <span className="text-muted small fw-bold">Power OFF</span>
                 </div>
               </div>
             </div>
         </div>
       </ModalHeader>
       <ModalBody className="px-8">
         <div className="row g-2 mb-3">
           <div className="col">
             <div className="border rounded p-2 text-center bg-light">
               <div className="text-muted small fs-6 fw-bold">Total Nodes</div>
               <div className="fw-bold fs-4">{counts.total}</div>
             </div>
           </div>

           <div className="col">
             <div className="border rounded p-2 text-center">
               <div className="text-muted small fs-6 fw-bold">Online</div>
               <div className="fw-bold fs-4 text-success">{counts.online}</div>
             </div>
           </div>

           <div className="col">
             <div className="border rounded p-2 text-center">
               <div className="text-muted small fs-6 fw-bold">Offline</div>

               <div className="fw-bold fs-4 text-danger">{counts.offline}</div>
             </div>
           </div>

           <div className="col">
             <div className="border rounded p-2 text-center">
               <div className="text-muted small fs-6 fw-bold">Power ON</div>
               <div className="fw-bold fs-4 text-success">{counts.powerOn}</div>
             </div>
           </div>

           <div className="col">
             <div className="border rounded p-2 text-center">
               <div className="text-muted small fs-6 fw-bold">Power OFF</div>
               <div className="fw-bold fs-4 text-danger">{counts.powerOff}</div>
             </div>
           </div>
         </div>
         <div className="mb-3">
           <div className="input-group">
             <span className="input-group-text bg-white">
               <i className="bx bx-search"></i>
             </span>

             <input
               type="text"
               className="form-control"
               placeholder="Search Node ID..."
               value={nodeSearch}
               onChange={(e) => setNodeSearch(e.target.value)}
             />

             {nodeSearch && (
               <button
                 type="button"
                 className="btn btn-outline-secondary"
                 onClick={() => setNodeSearch("")}
                 title="Clear search"
               >
                 <i className="bx bx-x"></i>
               </button>
             )}
           </div>
         </div>

         {selectedGroup?.driverType === "group" && (
           <GroupMembersTable members={groupMembers} />
         )}
       </ModalBody>
       <ModalFooter>
         <button
           type="button"
           className="btn btn-dark"
           onClick={toggleGroupModal}
         >
           Close
         </button>
       </ModalFooter>
     </Modal>
   </div>
 );
};

export const customStyles: any = {
  tableWrapper: {
    style: {
      minWidth: "1100px",
    },
  },
  subHeader: {
    style: {
      padding: "0px",
      margin: "0px",
    },
  },
  headRow: {
    style: {
      backgroundColor: "#EFF2F7",
      fontWeight: "bold",
      fontSize: "14px",
      borderBottom: "none",
    },
  },
  rows: {
    style: {
      fontSize: "14px",
      color: "#343a40",
      borderBottom: "none",
    },
  },
  cells: {
    style: {
      fontSize: "14px",
      color: "#343a40",
      borderBottom: "none",
    },
  },
};


export const ExpandCollapseIcon = ({ expanded }: { expanded: boolean }) => {
  return (
    <div
      className="d-flex justify-content-center align-items-center  "
      style={{ cursor: "pointer", width: "32px", height: "32px" }}
    >
      <i
        className={`bx ${
          expanded ? "bx-caret-down" : "bx-caret-right"
        } text-dark fs-5`}
      ></i>
    </div>
  );
};


